import { computed, onMounted, onUnmounted, shallowRef } from 'vue'
import type { CSSProperties } from 'vue'
import { APP_PAGE_GESTURE, APP_PAGE_LAYOUT, APP_PAGE_TIMING } from '@/constants/app'

interface UseAppPageCarouselOptions {
  slideCount: number
  getScale?: () => number
  getSwipeBoundaryTop?: () => number
  isOverlayOpen?: () => boolean
}

const GESTURE_AXIS_NONE = 'none'

export function useAppPageCarousel(options: UseAppPageCarouselOptions) {
  const activeIndex = shallowRef(0)
  const progress = shallowRef(0)
  const isDragging = shallowRef(false)
  const dragDeltaX = shallowRef(0)
  const isAnimating = shallowRef(true)
  const hasUserInteracted = shallowRef(false)
  const reducedMotion = shallowRef(false)
  const canSwipeGesture = shallowRef(false)
  const gestureAxis = shallowRef<'none' | 'x' | 'y'>(GESTURE_AXIS_NONE)
  const manualHoldProgress = shallowRef(false)
  const touchStartX = shallowRef(0)
  const touchStartY = shallowRef(0)
  const touchStartTime = shallowRef(0)

  let autoPlayTimer: ReturnType<typeof window.setTimeout> | null = null
  let autoPlayResumeTimer: ReturnType<typeof window.setTimeout> | null = null
  let autoPlayProgressFrame: number | null = null
  let autoPlayCycleStartTs = 0
  let autoPlayCycleDurationMs = APP_PAGE_TIMING.autoPlayIntervalMs
  let reducedMotionQuery: MediaQueryList | null = null

  const slidesStyle = computed<CSSProperties>(() => ({
    transform: `translateX(${
      -activeIndex.value * APP_PAGE_LAYOUT.carouselWidth + dragDeltaX.value
    }px)`,
    transition: isAnimating.value ? 'transform 0.3s ease' : 'none',
  }))

  function normalizeIndex(index: number) {
    if (options.slideCount <= 0) {
      return 0
    }

    return (index + options.slideCount) % options.slideCount
  }

  function setActiveSlide(index: number) {
    activeIndex.value = normalizeIndex(index)
    dragDeltaX.value = 0
    isAnimating.value = true
    updateActiveDotProgress(0)
  }

  function updateActiveDotProgress(nextProgress: number) {
    progress.value = Math.max(0, Math.min(nextProgress, 1))
  }

  function stopAutoPlayProgress() {
    if (autoPlayProgressFrame === null) {
      return
    }

    window.cancelAnimationFrame(autoPlayProgressFrame)
    autoPlayProgressFrame = null
  }

  function clearAutoPlay() {
    if (autoPlayTimer !== null) {
      window.clearTimeout(autoPlayTimer)
      autoPlayTimer = null
    }
  }

  function clearAutoPlayResume() {
    if (autoPlayResumeTimer !== null) {
      window.clearTimeout(autoPlayResumeTimer)
      autoPlayResumeTimer = null
    }
  }

  function shouldAutoPlay() {
    if (reducedMotion.value) {
      return false
    }

    if (typeof document !== 'undefined' && document.hidden) {
      return false
    }

    if (isDragging.value || options.isOverlayOpen?.()) {
      return false
    }

    return options.slideCount > 1
  }

  function startAutoPlayProgress() {
    stopAutoPlayProgress()
    updateActiveDotProgress(0)

    const tick = (ts: number) => {
      if (!autoPlayCycleStartTs || !autoPlayCycleDurationMs) {
        return
      }

      const elapsed = ts - autoPlayCycleStartTs
      const nextProgress = Math.min(elapsed / autoPlayCycleDurationMs, 1)
      updateActiveDotProgress(nextProgress)

      if (nextProgress >= 1 || autoPlayTimer === null) {
        return
      }

      autoPlayProgressFrame = window.requestAnimationFrame(tick)
    }

    autoPlayProgressFrame = window.requestAnimationFrame(tick)
  }

  function pauseAutoPlay() {
    clearAutoPlay()
    stopAutoPlayProgress()
  }

  function startAutoPlay(delayMs = APP_PAGE_TIMING.autoPlayIntervalMs) {
    clearAutoPlay()

    if (!shouldAutoPlay()) {
      return
    }

    autoPlayCycleStartTs = performance.now()
    autoPlayCycleDurationMs = delayMs
    startAutoPlayProgress()

    autoPlayTimer = window.setTimeout(() => {
      autoPlayTimer = null

      if (!shouldAutoPlay()) {
        return
      }

      stopAutoPlayProgress()
      setActiveSlide(activeIndex.value + 1)
      startAutoPlay(APP_PAGE_TIMING.autoPlayIntervalMs)
    }, delayMs)
  }

  function scheduleAutoPlayResume() {
    clearAutoPlayResume()

    if (reducedMotion.value) {
      return
    }

    autoPlayResumeTimer = window.setTimeout(() => {
      autoPlayResumeTimer = null

      if (!shouldAutoPlay()) {
        return
      }

      if (manualHoldProgress.value) {
        manualHoldProgress.value = false
        setActiveSlide(activeIndex.value + 1)
        startAutoPlay(APP_PAGE_TIMING.autoPlayIntervalMs)
        return
      }

      startAutoPlay(APP_PAGE_TIMING.autoPlayIntervalMs)
    }, APP_PAGE_TIMING.autoPlayResumeDelayMs)
  }

  function markUserInteraction() {
    hasUserInteracted.value = true
    manualHoldProgress.value = true
    pauseAutoPlay()
    updateActiveDotProgress(1)
    scheduleAutoPlayResume()
  }

  function pauseForOverlay() {
    pauseAutoPlay()
    clearAutoPlayResume()
  }

  function resumeAfterOverlay() {
    if (hasUserInteracted.value) {
      scheduleAutoPlayResume()
      return
    }

    startAutoPlay(APP_PAGE_TIMING.autoPlayIntervalMs)
  }

  function handleDotClick(index: number) {
    markUserInteraction()
    setActiveSlide(index)
    updateActiveDotProgress(1)
  }

  function resetGesture() {
    canSwipeGesture.value = false
    isDragging.value = false
    dragDeltaX.value = 0
    gestureAxis.value = GESTURE_AXIS_NONE
  }

  function handleTouchStart(event: TouchEvent) {
    const touch = event.changedTouches[0]

    if (!touch || options.isOverlayOpen?.()) {
      return
    }

    const boundaryTop = options.getSwipeBoundaryTop?.() ?? Number.POSITIVE_INFINITY
    canSwipeGesture.value = touch.clientY < boundaryTop

    if (!canSwipeGesture.value) {
      return
    }

    hasUserInteracted.value = true
    manualHoldProgress.value = true
    pauseAutoPlay()
    updateActiveDotProgress(1)
    isDragging.value = true
    isAnimating.value = false
    dragDeltaX.value = 0
    gestureAxis.value = GESTURE_AXIS_NONE
    touchStartX.value = touch.clientX
    touchStartY.value = touch.clientY
    touchStartTime.value = Date.now()
  }

  function handleTouchMove(event: TouchEvent) {
    if (!canSwipeGesture.value || !isDragging.value) {
      return
    }

    const touch = event.changedTouches[0]

    if (!touch) {
      return
    }

    const dx = touch.clientX - touchStartX.value
    const dy = touch.clientY - touchStartY.value

    if (gestureAxis.value === GESTURE_AXIS_NONE && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      gestureAxis.value = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'
    }

    if (gestureAxis.value !== 'x') {
      return
    }

    event.preventDefault()

    const scale = options.getScale?.() || 1
    let deltaInDesign = dx / scale

    if (
      (activeIndex.value === 0 && deltaInDesign > 0) ||
      (activeIndex.value === options.slideCount - 1 && deltaInDesign < 0)
    ) {
      deltaInDesign *= APP_PAGE_GESTURE.edgeResistance
    }

    dragDeltaX.value = deltaInDesign
  }

  function handleTouchEnd(event: TouchEvent) {
    if (!canSwipeGesture.value || !isDragging.value) {
      resetGesture()
      return
    }

    const touch = event.changedTouches[0]

    if (!touch) {
      resetGesture()
      return
    }

    const moveXViewport = touch.clientX - touchStartX.value
    const elapsed = Math.max(Date.now() - touchStartTime.value, 1)
    const velocity = Math.abs(moveXViewport) / elapsed
    const distanceThreshold = APP_PAGE_LAYOUT.carouselWidth * APP_PAGE_GESTURE.swipeDistanceRatio
    const shouldFlipByDistance = Math.abs(dragDeltaX.value) >= distanceThreshold
    const shouldFlipByVelocity = velocity >= APP_PAGE_GESTURE.swipeVelocityThreshold
    let nextIndex = activeIndex.value

    if (gestureAxis.value === 'x' && (shouldFlipByDistance || shouldFlipByVelocity)) {
      if (dragDeltaX.value < 0) {
        nextIndex = Math.min(activeIndex.value + 1, options.slideCount - 1)
      }

      if (dragDeltaX.value > 0) {
        nextIndex = Math.max(activeIndex.value - 1, 0)
      }
    }

    activeIndex.value = nextIndex
    dragDeltaX.value = 0
    isAnimating.value = true
    updateActiveDotProgress(1)
    resetGesture()
    scheduleAutoPlayResume()
  }

  function handleTouchCancel() {
    if (!canSwipeGesture.value || !isDragging.value) {
      resetGesture()
      return
    }

    dragDeltaX.value = 0
    isAnimating.value = true
    updateActiveDotProgress(1)
    resetGesture()
    scheduleAutoPlayResume()
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      pauseAutoPlay()
      clearAutoPlayResume()
      return
    }

    resumeAfterOverlay()
  }

  function handleReducedMotionChange() {
    reducedMotion.value = Boolean(reducedMotionQuery?.matches)

    if (reducedMotion.value) {
      pauseAutoPlay()
      clearAutoPlayResume()
      return
    }

    resumeAfterOverlay()
  }

  onMounted(() => {
    reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    reducedMotion.value = reducedMotionQuery.matches
    document.addEventListener('visibilitychange', handleVisibilityChange)

    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', handleReducedMotionChange)
    } else {
      reducedMotionQuery.addListener(handleReducedMotionChange)
    }

    startAutoPlay(APP_PAGE_TIMING.autoPlayFirstDelayMs)
  })

  onUnmounted(() => {
    pauseAutoPlay()
    clearAutoPlayResume()
    document.removeEventListener('visibilitychange', handleVisibilityChange)

    if (!reducedMotionQuery) {
      return
    }

    if (typeof reducedMotionQuery.removeEventListener === 'function') {
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange)
    } else {
      reducedMotionQuery.removeListener(handleReducedMotionChange)
    }
  })

  return {
    activeIndex,
    progress,
    slidesStyle,
    handleDotClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    markUserInteraction,
    pauseForOverlay,
    resumeAfterOverlay,
  }
}
